import type { Ruleset } from "./types.js";

/**
 * Emitter performance rules — the data behind the reuse-versus-replace decision.
 *
 * A radiator sized for a 70°C gas boiler is not the same radiator at a heat pump's
 * 45°C flow: it is roughly a third of it. Everything in this file exists so that
 * the ratio is computed from stated, versioned, citable numbers rather than from a
 * constant buried in the engine.
 *
 * PROVENANCE WARNING. Every ruleset here is marked "provisional". The values are
 * the ones the UK heating industry uses daily and they cross-check against widely
 * published secondary sources, but they have NOT been transcribed from the primary
 * BS EN 442 and BS EN 1264 PDFs — those are paid standards and are not among the
 * MCS documents held in the vault. Under the house rule (README, "Compliance mode
 * never runs on provisional data") the engine therefore refuses to lean on these
 * defaults for a compliance-grade result; a compliance run must supply the rated
 * output basis and the exponent from the product's own datasheet instead.
 */

/**
 * Emitter families that share a heat-output characteristic.
 *
 * The distinction that matters is natural versus forced convection. A panel
 * radiator's output rises faster than linearly with temperature difference because
 * the buoyancy-driven air flow over it also increases; a fan convector's air flow
 * is set by the fan, so its output is very nearly proportional to the temperature
 * difference. Using the panel exponent on a fan coil overstates its low-temperature
 * output badly, which is why they are separated here rather than lumped together.
 */
export type RadiatorKind =
  | "panel-radiator"
  | "column-radiator"
  | "towel-rail"
  | "natural-convector"
  | "fan-convector";

export interface RadiatorRatingBasis {
  /**
   * The temperature difference the manufacturer's quoted output applies at, K.
   *
   * BS EN 442 rates at ΔT50: mean water temperature 70°C against a 20°C room.
   * Watch for older UK datasheets rated to BS 3528 at ΔT60 (90/70/20) — the same
   * radiator carries a number about 30% larger there, and mistaking one for the
   * other is the single easiest way to get a reuse decision wrong.
   */
  readonly ratedDeltaTK: number;
  /**
   * How the mean water temperature is taken. BS EN 442 and UK domestic practice
   * use the ARITHMETIC mean, (flow + return) / 2. Some non-UK datasheets and most
   * heat-exchanger literature use the logarithmic mean, which gives a slightly
   * lower figure at the same flow and return. The engine implements arithmetic
   * only; a logarithmic-basis datasheet must be converted before it is used here.
   */
  readonly meanWaterTemperature: "arithmetic";
  /** The flow/return/room triple the rating is taken at, for the audit trail. */
  readonly referenceConditions: string;
}

export const EN_442_RATING_BASIS: Ruleset<RadiatorRatingBasis> = {
  id: "emitters.rating-basis",
  version: "0.1.0-provisional",
  effectiveFrom: "2014-11-30",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard: "BS EN 442-2",
    issue: "2014+A1:2019",
    // Descriptive rather than numbered: the clause number has not been read from
    // the standard itself, and inventing one would make an unchecked citation look
    // checked. Replace with the real clause when the PDF is obtained.
    clause: "Standard reference conditions and characteristic equation",
    issued: "2014-11-30",
  },
  data: {
    ratedDeltaTK: 50,
    meanWaterTemperature: "arithmetic",
    referenceConditions: "flow 75°C / return 65°C / room 20°C (mean water 70°C)",
  },
};

export interface RadiatorExponents {
  /**
   * The exponent n in output = rated × (ΔT_actual / ΔT_rated)^n.
   *
   * BS EN 442-2 does not publish a single value: n is a property of the individual
   * product, determined by test, and the better manufacturers publish it. These are
   * fallbacks for the common case where a surveyor is standing in front of a
   * twenty-year-old radiator with no datasheet.
   */
  readonly byKind: Record<RadiatorKind, number>;
  /** Used when the survey records an emitter but not which family it belongs to. */
  readonly defaultKind: RadiatorKind;
}

export const RADIATOR_EXPONENT_DEFAULTS: Ruleset<RadiatorExponents> = {
  id: "emitters.radiator-exponents",
  version: "0.1.0-provisional",
  effectiveFrom: "2014-11-30",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard: "BS EN 442-2",
    issue: "2014+A1:2019",
    clause: "Characteristic equation exponent (product-specific, determined by test)",
    issued: "2014-11-30",
  },
  data: {
    byKind: {
      // Steel panel radiators sit in a 1.2-1.35 band across manufacturers; 1.3 is
      // the value UK practice defaults to and the one MCS-adjacent guidance quotes.
      "panel-radiator": 1.3,
      // Cast iron and steel column radiators behave much like panels.
      "column-radiator": 1.3,
      // Towel rails are effectively narrow column radiators.
      "towel-rail": 1.3,
      // Cased natural convectors run higher — more of their output is convective,
      // and the convective share is the part that grows fastest with ΔT.
      "natural-convector": 1.4,
      // Forced convection: the air flow is set by the fan, not by buoyancy, so
      // output is close to proportional to ΔT. Applying 1.3 here would materially
      // overstate what a fan coil delivers at low flow temperatures.
      "fan-convector": 1.0,
    },
    defaultKind: "panel-radiator",
  },
};

/**
 * Which surface-temperature limit applies to a heated floor area.
 *
 * BS EN 1264-3 caps floor surface temperature for comfort and for the floor
 * covering, not for any hydraulic reason — which is why underfloor heating cannot
 * be pushed harder simply by raising the flow temperature.
 */
export type UnderfloorZone = "occupied" | "bathroom" | "peripheral";

export interface UnderfloorLimits {
  /**
   * The BS EN 1264-2 characteristic curve, q = C × (θ_floor − θ_room)^E W/m².
   * This is a property of the floor surface radiating and convecting into the room;
   * it is unrelated to the BS EN 442 radiator exponent and the two must never be
   * used interchangeably.
   */
  readonly characteristicCoefficient: number;
  readonly characteristicExponent: number;
  /**
   * BS EN 1264-3 maximum mean floor surface temperatures, °C.
   *
   * "peripheral" is the border strip within about a metre of an external wall,
   * where a higher surface temperature is permitted because it is not a standing
   * or sitting area. Applying the peripheral limit to a whole room would roughly
   * double its apparent capability, so the engine defaults to "occupied".
   */
  readonly maxSurfaceTempC: Record<UnderfloorZone, number>;
}

export const EN_1264_UNDERFLOOR_LIMITS: Ruleset<UnderfloorLimits> = {
  id: "emitters.underfloor-limits",
  version: "0.1.0-provisional",
  effectiveFrom: "2021-01-01",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard: "BS EN 1264-2 and BS EN 1264-3",
    issue: "2021",
    clause: "Characteristic curve and limiting floor surface temperatures",
    issued: "2021-01-01",
  },
  data: {
    characteristicCoefficient: 8.92,
    characteristicExponent: 1.1,
    maxSurfaceTempC: {
      occupied: 29,
      bathroom: 33,
      peripheral: 35,
    },
  },
};
